import { useState } from "react";

import { addDays, dayLabel } from "../domain/date";
import { ESTIMATE_LEGEND, dayShort, isEstimatedEntry } from "../domain/foodView";
import { planFor } from "../domain/intake";
import { type PlanTargets } from "../domain/planner";
import { copyBlocked, copyFromLine, copySource, planDayView, type PlanMeal } from "../domain/planView";
import type { FoodEntry, FoodItem, MealSlot } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import { copyIntoPlan, removePlanItem, saveTemplate } from "../store/toggles";
import { ActionBar } from "./ActionBar";
import { Button, IconButton } from "./Button";
import { DayMarkRows } from "./DayMarks";
import { EstimateLegend, Kcal } from "./Estimate";
import { I } from "./icons";
import { Insight } from "./Insight";
import { DiscloseRow, NavRow, RowGroup } from "./Rows";
import { Sheet } from "./Sheet";
import { Tag } from "./Tag";
import "./Plan.css";

/**
 * One planned day (FoodDayPlanned, FoodDayFast, FoodDayTwoMeals; ADR-043): each meal's heading on
 * the ground — planned of its share, the protein, and a `generated` tag when the planner made it —
 * over its own group of foods, each generated food with Swap, and the group's last row saying why
 * these, with Regenerate. Then the check, the day's marks — untracked, a fast day (board
 * FoodDaySheetMarks; ADR-049) — and copy and save.
 *
 * The person's own items carry no Swap: a plan never replaces them. They keep a remove, which the
 * boards do not draw — taking back something you placed has to stay possible.
 */
export function PlanDaySheet({
  ds,
  today,
  T,
  fast,
  lookup,
  canAdd,
  onClose,
  onAddFood,
  onSwap,
  onRegenerate,
}: {
  ds: string;
  today: string;
  T: PlanTargets | null;
  fast: boolean;
  lookup: (code: string) => FoodItem | null;
  canAdd: boolean;
  onClose: () => void;
  onAddFood: () => void;
  onSwap: (item: FoodEntry) => void;
  onRegenerate: (meal: MealSlot) => void;
}) {
  const { S, update, showToast } = useKaya();
  const [name, setName] = useState("");
  const v = planDayView(S, ds, today, T, { fast, lookup });
  const items = planFor(S, ds);
  const prev = addDays(ds, -1);
  const next = addDays(ds, 1);
  const source = copySource(S, prev, today);
  const prevFood = source.items;
  const blocked = copyBlocked(prevFood, prev, v.fast, v.fastName ?? "a fast", lookup);
  const twoMeal = v.skeleton.kind === "two";

  const copyIn = () => {
    let n = 0;
    update((d) => {
      n = copyIntoPlan(d, prevFood, ds);
    });
    showToast(n ? `Copied ${n} item${n === 1 ? "" : "s"} into ${dayLabel(ds)}` : "Nothing to copy — your own meals stay as they are");
  };
  const copyOut = () => {
    let n = 0;
    update((d) => {
      n = copyIntoPlan(d, items, next);
    });
    showToast(n ? `Copied to ${dayLabel(next)}` : `${dayLabel(next)} keeps the meals you placed`);
  };

  return (
    <Sheet
      title={dayLabel(ds)}
      sub={v.sub}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={canAdd ? { label: "Add food", onClick: onAddFood } : undefined}
          primary={{ label: "Done", onClick: onClose }}
        />
      }
    >
      {v.fast || (twoMeal && items.some((e) => e.id.startsWith("g:"))) ? (
        <div className="k-plan-daytag">{v.fast ? <Tag tone="fast" /> : <Tag tone="generated" />}</div>
      ) : null}

      <div className="k-plan-meals">
        {v.meals.map((m) => (
          <Meal
            key={m.slot.meal}
            m={m}
            onSwap={onSwap}
            onRemove={(e) => update((d) => removePlanItem(d, ds, e.id))}
            onRegenerate={() => onRegenerate(m.slot.meal)}
          />
        ))}
      </div>

      {items.some(isEstimatedEntry) ? <EstimateLegend>{ESTIMATE_LEGEND}</EstimateLegend> : null}

      {v.check ? (
        <RowGroup heading="Check">
          <Insight first insight={{ key: "plan.check", text: v.check.line, basedOn: v.check.basedOn.replace(/^based on /, "") }} />
          {v.check.second ? (
            <Insight
              insight={{ key: "plan.kcal", text: v.check.second.line, basedOn: v.check.second.basedOn.replace(/^based on /, "") }}
            />
          ) : null}
        </RowGroup>
      ) : null}

      <DayMarkRows ds={ds} />

      <RowGroup ariaLabel="Copy and save">
        {blocked ? (
          <NavRow kind="disabled" icon={I.list} title="Copy yesterday’s food into this day" subtitle={blocked} />
        ) : (
          <NavRow
            icon={I.list}
            title="Copy yesterday’s food into this day"
            subtitle={copyFromLine(source, prev, today)}
            onClick={prevFood.length ? copyIn : undefined}
          />
        )}
        <NavRow
          icon={I.calendar}
          title="Copy this day to tomorrow"
          subtitle={`${dayShort(next)} · replaces generated meals, keeps yours`}
          onClick={items.length ? copyOut : undefined}
        />
        {items.length ? (
          <DiscloseRow
            icon={I.book}
            title="Save as template"
            subtitle={v.fast && v.fastName ? `Name it to reuse on the next ${v.fastName}` : "Name this day to reuse it"}
          >
            <div className="k-food-row">
              <input
                className="k-food-input"
                aria-label="Template name"
                value={name}
                placeholder={v.fast && v.fastName ? v.fastName : "Office day"}
                onChange={(e) => setName(e.target.value)}
              />
              <Button
                disabled={!name.trim()}
                onClick={() => {
                  update((d) => {
                    saveTemplate(d, { name, items });
                  });
                  showToast(`Saved ${name.trim()}`);
                  setName("");
                }}
              >
                Save
              </Button>
            </div>
          </DiscloseRow>
        ) : (
          <NavRow kind="disabled" icon={I.book} title="Save as template" subtitle="Plan some food for this day first" />
        )}
      </RowGroup>
    </Sheet>
  );
}

function Meal({
  m,
  onSwap,
  onRemove,
  onRegenerate,
}: {
  m: PlanMeal;
  onSwap: (e: FoodEntry) => void;
  onRemove: (e: FoodEntry) => void;
  onRegenerate: () => void;
}) {
  const label = m.slot.label;
  return (
    <section className="k-plan-meal" aria-label={label}>
      <div className="k-food-mealhead">
        <div className="k-food-mealhead__txt">
          <h3 className="k-food-mealhead__h">{m.heading}</h3>
          <p className="k-food-mealhead__sub">{m.sub}</p>
        </div>
        {m.generated ? <Tag tone="generated" /> : null}
      </div>
      <div className="k-rows">
        {!m.rows.length ? (
          <div className="k-row k-food-empty">
            <span className="k-food-empty__txt">{m.slot.budgetOnly ? m.reason ?? "Left as a budget" : "Nothing planned"}</span>
          </div>
        ) : null}
        {m.rows.map((r) => (
          <div className="k-row k-plan-item" key={r.entry.id}>
            <span className="k-plan-item__txt">
              <span className="k-plan-item__name">
                <span>{r.entry.name}</span>
                {r.template ? <Tag tone="template" /> : null}
              </span>
              <span className="k-plan-item__sub">{r.sub}</span>
            </span>
            <span className="k-plan-item__kcal">
              <Kcal kcal={r.entry.kcal} estimated={isEstimatedEntry(r.entry)} />
            </span>
            {r.generated ? (
              <Button variant="ghost" onClick={() => onSwap(r.entry)} ariaLabel={`Swap ${r.entry.name}`}>
                Swap
              </Button>
            ) : (
              <IconButton icon={I.close} label={`Remove ${r.entry.name} from the plan`} onClick={() => onRemove(r.entry)} />
            )}
          </div>
        ))}
        {m.generated && m.reason ? (
          <div className="k-row k-plan-foot">
            <span className="k-plan-foot__why">{m.reason}</span>
            <Button variant="ghost" icon={I.refresh} onClick={onRegenerate} ariaLabel={`Regenerate ${label.toLowerCase()}`}>
              Regenerate
            </Button>
          </div>
        ) : null}
        {m.note ? (
          <div className="k-row k-plan-foot">
            <p className="k-plan-note">{m.note}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
